/**
 * Grab a still frame from a video File and return it as a Blob/PNG.
 *
 * Used to auto-generate an exercise thumbnail the moment an admin
 * uploads a video, so cards in the library never fall back to the
 * generic dumbbell icon. Runs 100% client-side (no ffmpeg).
 *
 * @param {File} file   The uploaded video File object.
 * @param {number} atSec Seconds into the video to capture (default 0.5s).
 * @returns {Promise<Blob>} PNG blob of the captured frame.
 */
export const captureVideoFrame = (file, atSec = 0.5) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'auto';
    video.muted = true;
    video.playsInline = true;
    video.crossOrigin = 'anonymous';
    video.src = url;

    const cleanup = () => URL.revokeObjectURL(url);

    const onError = (e) => {
      cleanup();
      reject(e instanceof Error ? e : new Error('Video frame capture failed'));
    };

    video.addEventListener('loadedmetadata', () => {
      // Clamp to within the clip so very short videos still work.
      video.currentTime = Math.min(atSec, Math.max(0, (video.duration || 1) - 0.1));
    });

    video.addEventListener('seeked', () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth || 720;
        canvas.height = video.videoHeight || 480;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => {
          cleanup();
          if (!blob) return reject(new Error('Frame blob empty'));
          resolve(blob);
        }, 'image/png', 0.92);
      } catch (err) {
        onError(err);
      }
    });

    video.addEventListener('error', onError);
  });
